import type { CompInfo } from '@wix/thunderbolt-becky-root'
import { VerticalMenuCSSVars, VerticalMenuDefinition } from './VerticalMenu'
import { withCompInfo } from '@wix/editor-elements-integrations'
import { color } from '@wix/thunderbolt-commons'
import { getComboBoxInputNavigationCSSVars } from '../../core/mappers/MenuItems/MenuItems.utils'

const { isThemeColor, getFromColorMap, getRGBAColorString } = color

const extractBorderColor = (siteColors: CompInfo['siteColors'], styleProperties: CompInfo['styleProperties']) => {
	const colorBrd = styleProperties.brd
	const opacity = Number(styleProperties['alpha-brd'])
	const myColor = isThemeColor(colorBrd) ? getFromColorMap(colorBrd, siteColors) : colorBrd
	return isThemeColor(colorBrd) ? getRGBAColorString(myColor, opacity) : colorBrd
}

const comboBoxInputCss = (
	compInfo: Pick<
		CompInfo,
		| 'compLayout'
		| 'styleProperties'
		| 'siteFonts'
		| 'isMobileView'
		| 'compProps'
		| 'siteColors'
		| 'isOneDocMigrated'
	>
) => {
	const { compProps, styleProperties, siteColors, isOneDocMigrated } = compInfo
	const borders = {
		'--borderColor': extractBorderColor(siteColors, styleProperties),
		'--borderWidth': styleProperties.brw,
		'--arrowBorderWidth': '0',
	}

	return getComboBoxInputNavigationCSSVars(compInfo, compProps.itemsAlignment, borders, isOneDocMigrated)
}

const itemsAlignmentOptions: Record<string, string> = {
	right: 'flex-end',
	left: 'flex-start',
	center: 'center',
}

const getSeparatorHeightAdjusted = (styleProperties: CompInfo['styleProperties']) => {
	// Backward compatibility with Bolt behaviour:
	// For a separator to appear it has to be > 2px
	// When a separator appears we reduce it by 2px
	const separatorHeight = styleProperties.separatorHeight
	const separatorHeightAsNum = parseInt((separatorHeight || '0').split('px')[0], 10)
	return `${separatorHeightAsNum === 0 ? 0 : separatorHeightAsNum > 2 ? separatorHeightAsNum - 2 : 0}px`
}

const getItemHeight = (
	styleProperties: CompInfo['styleProperties'],
	skin: CompInfo['skin'],
	menuItemHeight: number
) => {
	// Backward compatibility with Bolt behaviour:
	// Normally itemHeight = menuItemHeight, except for 2 cases:
	// 1. itemHeight = menuItemHeight + 2 when borderWidth > 0, separatorHeight = 0px and not VerticalMenuSolidColorSkin
	// 2. itemHeight = menuItemHeight + 1 when borderWidth > 0 and separatorHeight = 1px
	const separatorHeight = styleProperties.separatorHeight
	const borderWidth = styleProperties.brw
	const borderWidthAsNum = parseInt((borderWidth || '0').replace('px', ''), 10)
	return separatorHeight === '0px' && borderWidthAsNum > 0
		? skin === 'VerticalMenuSolidColorSkin'
			? menuItemHeight
			: menuItemHeight + 2
		: separatorHeight === '1px' && borderWidthAsNum > 0
			? menuItemHeight + 1
			: menuItemHeight
}

const defaultCss = (compInfo: Pick<CompInfo, 'compProps' | 'styleProperties' | 'skin'>): VerticalMenuCSSVars => {
	const { compProps, styleProperties, skin } = compInfo
	const { menuItemHeight, subMenuOpenSide, itemsAlignment } = compProps
	const subMenuOpenDirectionRight = subMenuOpenSide === 'right' ? 'auto' : '0'
	const subMenuOpenDirectionLeft = subMenuOpenSide === 'right' ? '0' : 'auto'

	const itemHeight = `${menuItemHeight === null ? 50 : getItemHeight(styleProperties, skin, menuItemHeight)}px`

	const textAlign = itemsAlignmentOptions[itemsAlignment] as 'center' | 'flex-end' | 'flex-start'
	const separatorHeightAdjusted = getSeparatorHeightAdjusted(styleProperties)

	return {
		'--item-height': itemHeight,
		'--item-align': itemsAlignment,
		'--text-align': textAlign,
		'--sub-menu-open-direction-right': subMenuOpenDirectionRight,
		'--sub-menu-open-direction-left': subMenuOpenDirectionLeft,
		'--separator-height-adjusted': separatorHeightAdjusted,
	}
}

export const css = withCompInfo<VerticalMenuCSSVars, VerticalMenuDefinition>()(
	[
		'compLayout',
		'styleProperties',
		'siteFonts',
		'isMobileView',
		'compProps',
		'skin',
		'uiType',
		'siteColors',
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		'isOneDocMigrated',
	],
	({
		compLayout,
		styleProperties,
		siteFonts,
		isMobileView,
		compProps,
		skin,
		uiType,
		siteColors,
		// @ts-ignore - Remove once isOneDocMigrated is added to the compInfo
		isOneDocMigrated,
	}) => {
		const compInfo = {
			compLayout,
			styleProperties,
			siteFonts,
			isMobileView,
			compProps,
			skin,
			siteColors,
			isOneDocMigrated,
		}
		return uiType === 'ComboBoxInputNavigation' ? comboBoxInputCss(compInfo) : defaultCss(compInfo)
	}
)
